import {
  dissoc, assoc, equals, cond,
} from 'ramda';
import { ClassicPreset } from 'rete';
import { INPUT_TYPES } from 'app-constants/inputTypes';
import {
  ACTION_TYPES, CALL_API, CALL_FUNCTION,
  COMMENT, MAP_RESULT, SEND_MESSAGE,
} from 'app-constants/actionTypes';
import { VALUE_TIME_CAP, VALUE_TIME_OUT } from 'app-constants/apiIntegrations';
import { isJson } from 'utils';
import {
  createNode as createReteNode, getNodeSocket, nextNodeId, nextNodeTitle,
} from 'modules/VisualBuilder/components/rete2/nodeFactory';
import { getBotspecInfo } from '../selectors';
import { updateBotspecs } from './chatbots';
import {
  createText, createEmail, createPassword,
  createCheckbox, createRadio, createMedia,
  createDate, createLocation, createPhone,
  createNumber, createBarcode, createSignature,
} from './inputTypes';
import slugify from './utils';
import logger from 'utils/logger';

const createSendMessageProperties = () => ({
  title: '',
  type: 'send_message',
  vars: {},
  messages: {},
  input: {},
  mediaUrl: '',
  metadata: {
    media: '',
  },
  closeChat: false,
  delay: 0,
  resultVariables: {},
});

const createCallApiProperties = () => ({
  type: 'call_api',
  vars: {},
  method: '',
  content_type: '',
  url: '',
  params: {},
  headers: {},
  body: '',
  timeout: VALUE_TIME_OUT,
  postVerify: {
    timecap: VALUE_TIME_CAP,
    status: ['200 OK', '201 Created'],
    schema: JSON.stringify({
      type: 'object',
    }),
  },
  resultVariables: {},
});

const createMapResultProperties = () => ({
  title: '',
  type: 'map_result',
  vars: {},
  targets: {},
});

const createCallFunctionProperties = () => ({
  type: 'call_function',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createEvalNextProperties = () => ({
  type: 'eval_next',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createConditionalStepProperties = () => ({
  type: 'conditional_step',
  vars: {},
  eval: {},
  resultVariables: {},
  conditions: {},
  isEvalFunction: true,
});

const createStoreDataProperties = () => ({
  type: 'store_data',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createStoreFileProperties = () => ({
  type: 'store_file',
  vars: {},
  eval: {},
  resultVariables: {},
});

const createCommentProperties = () => ({
  type: COMMENT,
  text: '',
});

export const addNode = (nodeId, type) => (dispatch, getState, getContext) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  const nodes = botspec.rete_json.data;
  const { editor, area } = getContext();
  if (nodes && !nodes[nodeId]) {
    const createProperty = cond([
      [equals(ACTION_TYPES.SEND_MESSAGE), createSendMessageProperties],
      [equals(ACTION_TYPES.CALL_API), createCallApiProperties],
      [equals(ACTION_TYPES.MAP_RESULT), createMapResultProperties],
      [equals(ACTION_TYPES.CALL_FUNCTION), createCallFunctionProperties],
      [equals(ACTION_TYPES.EVAL_NEXT), createEvalNextProperties],
      [equals(ACTION_TYPES.STORE_DATA), createStoreDataProperties],
      [equals(ACTION_TYPES.STORE_FILE), createStoreFileProperties],
      [equals(ACTION_TYPES.CONDITIONAL_STEP), createConditionalStepProperties],
      [equals(COMMENT), createCommentProperties],
    ]);
    const reteNode = editor?.getNodes ? editor.getNodes().find((n) => n.id === nodeId) : null;
    const properties = { ...createProperty(type), title: reteNode?.data?.title };
    const data = { ...nodes, [nodeId]: { properties, connections: {} } };
    const reteJson = { ...botspec.rete_json, data };
    if (reteNode?.data) {
      reteNode.data.properties = { ...reteNode.data.properties, ...properties };
      area?.update('node', reteNode.id);
    }

    dispatch(
      updateBotspecs({
        ...botspecs,
        [selectedBotspec]: {
          ...botspec,
          rete_json: reteJson,
        },
      }),
    );
  }
};

export const deleteNode = (nodeId) => (dispatch, getState) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  let { rete_json: reteJson } = botspec;
  const nodes = reteJson.data;

  if (reteJson.entryPoint === nodeId) {
    reteJson = { ...reteJson, entryPoint: '' };
  }
  const data = dissoc(nodeId, nodes);
  const updatedReteJson = { ...reteJson, data };
  dispatch(updateBotspecs(
    { ...botspecs, [selectedBotspec]: { ...botspec, rete_json: updatedReteJson } },
  ));
};

const formatMapResultVariables = (oldTitle, newTitle, oldNodeId, nodeId, properties) => {
  const oldTransitionId = slugify(`${oldTitle}${oldNodeId}`);
  const newTransitionId = slugify(`${newTitle}${nodeId}`);

  let updatedResultVariables = properties.resultVariables;
  if (properties.resultVariables) {
    updatedResultVariables = Object.keys(properties.resultVariables).reduce((accum, key) => ({
      ...accum,
      [key]: {
        ...properties.resultVariables[key],
        reference: properties.resultVariables[key].reference.replace(
          oldTransitionId, newTransitionId,
        ),
      },
    }), properties.resultVariables);
  }

  return { ...properties, resultVariables: updatedResultVariables };
};

export const saveNodeProperties = (nodeId, properties) => (
  dispatch,
  getState,
  getContext,
) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  const { editor, area } = getContext();
  const reteNode = editor?.getNodes ? editor.getNodes().find((node) => node.id === nodeId) : null;
  const nodes = botspec.rete_json.data;
  let node = nodes[nodeId];
  const oldTitle = node.properties.title;
  const newTitle = properties.title;
  const updatedProperties = formatMapResultVariables(
    oldTitle, newTitle, nodeId, nodeId, properties,
  );
  node = assoc('properties', updatedProperties, node);
  if (reteNode?.data) {
    const title = properties.title || reteNode.data.title;
    reteNode.data.properties = updatedProperties;
    reteNode.data.title = title;
    area?.update('node', reteNode.id);
  }
  const data = { ...nodes, [nodeId]: { ...node } };
  const reteJson = { ...botspec.rete_json, data };
  dispatch(updateBotspecs({ ...botspecs, [selectedBotspec]: { ...botspec, rete_json: reteJson } }));
};

// En Rete 2 las conexiones viven en el editor, no en el objeto output.
const deleteOutputConnections = (editor, nodeId, outputKey) => {
  editor.getConnections()
    .filter((c) => c.source === nodeId && c.sourceOutput === outputKey)
    .forEach((c) => { editor.removeConnection(c.id); });
};

export const deleteNodeOutput = (nodeId, keys) => async (dispatch, getState, getContext) => {
  const { editor, area } = getContext();
  const reteNode = editor?.getNodes ? editor.getNodes().find((n) => n.id === nodeId) : null;
  if (!reteNode) return;

  keys.forEach((key) => {
    if (reteNode.hasOutput(key)) {
      deleteOutputConnections(editor, nodeId, key);
      reteNode.removeOutput(key);
    }
  });
  if (reteNode.data.outputs?.length > 0) {
    reteNode.data.outputs = reteNode.data.outputs.filter((o) => !keys.includes(o));
  }
  if (Object.keys(reteNode.outputs).length === 0 && !keys.includes('nxt')) {
    reteNode.addOutput('nxt', new ClassicPreset.Output(getNodeSocket(reteNode), 'Next'));
  }
  area?.update('node', reteNode.id);
};

export const deleteNodeOutputs = (nodeId) => (dispatch, getState, getContext) => {
  const { editor, area } = getContext();
  const reteNode = editor?.getNodes ? editor.getNodes().find((n) => n.id === nodeId) : null;
  if (!reteNode?.data?.outputs?.length) return;

  const hadDefaultOutput = reteNode.hasOutput('nxt');
  reteNode.data.outputs.forEach((key) => {
    deleteOutputConnections(editor, nodeId, key);
    if (reteNode.hasOutput(key)) reteNode.removeOutput(key);
  });
  reteNode.data.outputs = [];

  if (!hadDefaultOutput) {
    reteNode.data.outputs.push('nxt');
    reteNode.addOutput('nxt', new ClassicPreset.Output(getNodeSocket(reteNode), 'Next'));
  }
  area?.update('node', reteNode.id);
};

export const addNodeOutput = (nodeId, key, label) => async (dispatch, getState, getContext) => {
  const { editor, area } = getContext();
  const reteNode = editor?.getNodes ? editor.getNodes().find((n) => n.id === nodeId) : null;
  if (!reteNode) return;

  if (reteNode.hasOutput('nxt')) {
    dispatch(deleteNodeOutput(nodeId, ['nxt']));
  }

  if (!reteNode.data.outputs) reteNode.data.outputs = [];

  if (!reteNode.data.outputs.includes(key)) {
    reteNode.data.outputs.push(key);
    reteNode.data.outputsLabels = { ...reteNode.data?.outputsLabels, [key]: label };
    reteNode.addOutput(key, new ClassicPreset.Output(getNodeSocket(reteNode), label));
    area?.update('node', reteNode.id);
  }
};

export const setEntryPoint = (nodeId) => (dispatch, getState, getContext) => {
  const { selectedBotspec, botspecs, botspec } = getBotspecInfo(getState());
  const { editor, area } = getContext();
  const allNodes = editor?.getNodes ? editor.getNodes() : [];
  const reteNode = allNodes.find((n) => n.id === nodeId);
  const remainingNodes = allNodes.filter((n) => n.id !== nodeId);

  if (reteNode?.data) {
    reteNode.data = { ...reteNode.data, isEntryPoint: true };
    area?.update('node', reteNode.id);
  }
  remainingNodes.forEach((n) => {
    if (n.data) {
      n.data = { ...n.data, isEntryPoint: false };
      area?.update('node', n.id);
    }
  });

  const reteJson = { ...botspec.rete_json, entryPoint: nodeId };

  dispatch(
    updateBotspecs({ ...botspecs, [selectedBotspec]: { ...botspec, rete_json: reteJson } }),
  );
};

export const createInputTypeProperty = (nodeId, properties, type) => (dispatch) => {
  const createProperty = cond([
    [equals(INPUT_TYPES.TEXT), createText],
    [equals(INPUT_TYPES.EMAIL), createEmail],
    [equals(INPUT_TYPES.PASSWORD), createPassword],
    [equals(INPUT_TYPES.CHECKBOX), createCheckbox],
    [equals(INPUT_TYPES.RADIO), createRadio],
    [equals(INPUT_TYPES.MEDIA), createMedia],
    [equals(INPUT_TYPES.DATE), createDate],
    [equals(INPUT_TYPES.LOCATION), createLocation],
    [equals(INPUT_TYPES.PHONE), createPhone],
    [equals(INPUT_TYPES.NUMBER), createNumber],
    [equals(INPUT_TYPES.BARCODE), createBarcode],
    [equals(INPUT_TYPES.SIGNATURE), createSignature],
  ]);

  const inputType = createProperty(type);
  const updatedProperties = { ...properties, input: inputType };
  dispatch(saveNodeProperties(nodeId, updatedProperties));
};

const getCallApiOutputType = (actionProperties) => {
  const { schema: jsonSchema } = actionProperties.postVerify;
  /** @type {any} El schema puede venir como texto JSON o ya como objeto. */
  let schema = '{}';
  try {
    schema = isJson(jsonSchema) ? JSON.parse(jsonSchema) : jsonSchema;
    return schema.type;
  } catch (err) {
     
    logger.error(err);
  }

  return '';
};

const getCallFunctionOutputType = (actionProperties) => {
  const { outputType, schema: schemaString } = actionProperties.eval;

  let schema;
  if (schemaString?.length > 0) {
    try {
      schema = JSON.parse(schemaString);
    } catch {
      // Ignore
    }
  }

  return schema?.type || outputType;
};

export const getActionAnswerType = (actionProperties) => {
  const getActionOutputTypeCalculator = {
    [CALL_API]: getCallApiOutputType,
    [CALL_FUNCTION]: getCallFunctionOutputType,
  };

  return getActionOutputTypeCalculator[actionProperties.type](actionProperties);
};

const formatExplicitMapResultVariables = (oldTitle, newTitle, oldNodeId, nodeId, properties) => {
  const oldTransitionId = slugify(`${oldTitle}${oldNodeId}`);
  const newTransitionId = slugify(`${newTitle}${nodeId}`);

  const { targets } = properties;
  const updatedTargets = Object.keys(targets).reduce(
    (accum, targetKey) => {
      const target = targets[targetKey];
      return {
        ...accum,
        [targetKey]: {
          from: target.from.replace(oldTransitionId, newTransitionId),
          assignments: target.assignments.reduce((assngAccum, assignment) => [
            ...assngAccum,
            {
              map: assignment.map.replace(oldTransitionId, newTransitionId),
              to: assignment.to.replace(oldTransitionId, newTransitionId),
            },
          ], []),
        },
      };
    }, {},
  );

  return { ...properties, targets: updatedTargets };
};

const replaceInputTypeIds = (data) => {
  const { properties } = data;
  if (properties.type === SEND_MESSAGE) {
    const { data: inputData, type: inputType } = properties.input;
    if (inputType === INPUT_TYPES.RADIO || inputType === INPUT_TYPES.CHECKBOX) {
      const updatedInputData = Object.keys(inputData).reduce((accum, key) => ({
        ...accum,
        [key]: { ...inputData[key], id: `${Math.random().toString(36).substring(2, 9)}` },
      }), {});

      return {
        ...data,
        properties: {
          ...data.properties,
          input: {
            ...data.properties.input,
            data: updatedInputData,
          },
        },
      };
    }
  }

  return data;
};

// Reemplaza los ids de outputs para un nodo clonado de tipo SEND_MESSAGE con radio/checkbox.
// En Rete 2, reteNode.outputs es un objeto plano {key: Output}, no un Map.
const replaceNodeOutputIds = (reteNode, nodedata, srcOutputsObj) => {
  const { properties } = nodedata;

  if (srcOutputsObj && properties.type === SEND_MESSAGE) {
    const { type: inputType } = properties.input;
    if (inputType === INPUT_TYPES.RADIO || inputType === INPUT_TYPES.CHECKBOX) {
      if (reteNode.hasOutput('nxt')) {
        reteNode.removeOutput('nxt');
      }
      Object.values(srcOutputsObj).forEach((srcOutput) => {
        const randomKey = `${Math.random().toString(36).substring(2, 9)}`;
        reteNode.addOutput(randomKey, new ClassicPreset.Output(getNodeSocket(reteNode), srcOutput.label));
      });
      return reteNode.outputs; // plain object {key: Output}
    }
  }

  return srcOutputsObj;
};

export const cloneNode = (node, params) => async (dispatch, getState, getContext) => {
  const { botspec } = getBotspecInfo(getState());
  const { editor, area } = getContext();
  // node puede ser un nodo v2 completo (con .label) o solo { id }
  const label = node.label || node.name;
  const allNodes = editor?.getNodes ? editor.getNodes() : [];
  const { data } = botspec.rete_json;
  const nodeData = data[node.id];
  if (!nodeData || !label) return;

  const clonedData = JSON.parse(JSON.stringify(nodeData));
  const updatedClonedData = replaceInputTypeIds(clonedData);
  const updatedTitle = nextNodeTitle(label, allNodes);

  // Crea el nodo Rete 2 con los datos del clon y agrégalo al editor
  const clonedReteNode = createReteNode(label, {
    ...updatedClonedData,
    title: updatedTitle,
    isEntryPoint: false,
  });

  clonedReteNode.id = nextNodeId(allNodes);
  await editor.addNode(clonedReteNode);
  if (area) {
    await area.translate(clonedReteNode.id, { x: params.positionX, y: params.positionY });
  }

  // Reemplaza outputs dinámicos (radio/checkbox) con nuevas claves
  const srcOutputs = node.outputs || {};
  const updatedOutputs = replaceNodeOutputIds(clonedReteNode, nodeData, srcOutputs);

  const nodeLabels = Object.entries(updatedOutputs).reduce((acc, [key, out]) => ({
    ...acc, [key]: out.label,
  }), {});

  clonedReteNode.data = {
    ...clonedReteNode.data,
    outputs: Object.keys(updatedOutputs),
    outputsLabels: nodeLabels,
  };

  const { type } = updatedClonedData.properties;
  const transitionMapperFunction = type === MAP_RESULT
    ? formatExplicitMapResultVariables : formatMapResultVariables;

  const updatedProperties = transitionMapperFunction(
    node.data?.title, updatedTitle, node.id, clonedReteNode.id, updatedClonedData.properties,
  );

  if (
    updatedProperties.type === SEND_MESSAGE
    && (updatedProperties.input.type === INPUT_TYPES.RADIO
      || updatedProperties.input.type === INPUT_TYPES.CHECKBOX)
  ) {
    Object.entries(updatedOutputs).forEach(([key, out]) => {
      Object.keys(updatedProperties.input.data).forEach((input) => {
        if (updatedProperties.input?.data[input]?.name === out.label) {
          updatedProperties.input.data[input].id = key;
        }
      });
    });
  }

  dispatch(saveNodeProperties(clonedReteNode.id, {
    ...updatedProperties,
    title: updatedTitle,
  }));
  area?.update('node', clonedReteNode.id);
};
